import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
export const frontmatter = {
  "title": "Finance",
  "lead": "One in three countries did not reach either of the finance benchmarks set in the Education 2030 Framework for Action."
};
function _createMdxContent(props) {
  const _components = {
    a: "a",
    blockquote: "blockquote",
    h2: "h2",
    li: "li",
    p: "p",
    strong: "strong",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  }, {Figure, HighlightByIndex, HighlightByPalette, IndicatorReference, RelatedMaterial, ScrollyArea, ScrollySection, ScrollySticky} = _components;
  if (!Figure) _missingMdxReference("Figure", true);
  if (!HighlightByIndex) _missingMdxReference("HighlightByIndex", true);
  if (!HighlightByPalette) _missingMdxReference("HighlightByPalette", true);
  if (!IndicatorReference) _missingMdxReference("IndicatorReference", true);
  if (!RelatedMaterial) _missingMdxReference("RelatedMaterial", true);
  if (!ScrollyArea) _missingMdxReference("ScrollyArea", true);
  if (!ScrollySection) _missingMdxReference("ScrollySection", true);
  if (!ScrollySticky) _missingMdxReference("ScrollySticky", true);
  return _jsxs(_Fragment, {
    children: [_jsx(Figure, {
      id: "HERO_FINANCE"
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsx(_components.p, {
        children: "The ambitious SDG education targets will not be met without additional resources, especially in countries that are the furthest behind."
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "The GEM report calculates that in order to achieve the 2030 target for universal basic education, i.e. pre-primary, primary and secondary education, low- and lower-middle-income countries need to spend US$461 billion annually. Many countries are unlikely to increase budgets adequately due to low tax revenues, resulting in an estimated annual financing gap of US$97 billion (21% of the total cost) for achieving national targets between 2023 and 2030. The average gap is US$26 billion (50% of the total) in low-income countries and US$71 billion (17%) in lower-middle-income countries, equivalent to 2.3% of GDP during this period."
    }), "\n", _jsx(IndicatorReference, {
      id: "4.5.3",
      children: _jsxs(_components.p, {
        children: ["Having more resources is not necessarily enough, though; countries spending the same amount in terms of income per capita can have wildly different education results depending on whether they are spending effectively, efficiently and equitably. For instance, out of 195 countries, only 8%  has a strong ", _jsx(_components.a, {
          href: "https://unesdoc.unesco.org/ark:/48223/pf0000397618_eng",
          children: "equity focus through financing policies"
        }), ". They were mostly high-income and a few upper-middle income countries in Latin America and Europe and Northern America."]
      })
    }), "\n", _jsxs(_components.p, {
      children: ["The three main sources of education spending are governments, households and donors. This page will focus on the first two. A separate page is devoted to ", _jsx(_components.a, {
        href: "https://www.education-progress.org/en/articles/finance-aid",
        children: "aid to education"
      }), "."]
    }), "\n", _jsxs(ScrollyArea, {
      children: [_jsx(ScrollySticky, {
        children: _jsx(Figure, {
          id: "TOTBYSOURCE"
        })
      }), _jsx(ScrollySection, {
        id: "absolute,0",
        children: _jsx(_components.p, {
          children: "In 2022, of the US$ 5.8 trillion spent on education worldwide, only 0.45% is spent in low income countries, while 64% is spent in high income countries, even though the two groups have a roughly equal number of school-age children."
        })
      }), _jsx(ScrollySection, {
        id: "relative,1",
        children: _jsxs(_components.p, {
          children: ["In the median country, ", _jsx(HighlightByPalette, {
            palette: "06",
            index: "1",
            children: "households"
          }), " are covering a quarter of the cost of education. In fact, households pick up a much larger education bill in lower-middle-income countries (44%) than in high-income countries (20%)."]
        })
      }), _jsx(ScrollySection, {
        id: "relative,2",
        children: _jsxs(_components.p, {
          children: ["Although ", _jsx(HighlightByPalette, {
            palette: "06",
            index: "8",
            children: "donors"
          }), " contribute 12% of total education spending in low income countries …"]
        })
      }), _jsx(ScrollySection, {
        id: "absolute,3",
        children: _jsxs(_components.p, {
          children: ["… ", _jsx(HighlightByPalette, {
            palette: "06",
            index: "8",
            children: "donors'"
          }), " contributions account for merely 0.3% of total spending globally, which is barely visible in absolute amounts."]
        })
      })]
    }), "\n", _jsx(_components.h2, {
      id: "public-expenditure",
      children: "Public expenditure"
    }), "\n", _jsxs(IndicatorReference, {
      id: "F.1",
      children: [_jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "https://unesdoc.unesco.org/ark:/48223/pf0000245656",
          children: "Education 2030 Framework for Action"
        }), " has set two key finance benchmarks for governments. It calls for them to:"]
      }), _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "allocate at least 4% to 6% of GDP to education, and/or"
        }), "\n", _jsx(_components.li, {
          children: "allocate at least 15% to 20% of public expenditure to education."
        }), "\n"]
      }), _jsx(_components.p, {
        children: "Globally, governments have not strayed far from these norms. The median government spending is 4% of GDP and 12.6% of total public expenditure. Governments in poorer countries with low capacity to generate domestic resources but with large cohorts of children tend to spend less on education as a share of GDP but more as a share of total government spending."
      })]
    }), "\n", _jsxs(IndicatorReference, {
      id: "F.2",
      children: [_jsx(_components.p, {
        children: "In Sub-Saharan Africa, government spending on education represents 15.5% of total public expenditure, but 3.5% of GDP. Conversely, Europe and Northern America meets the benchmark as a share of GDP (4.8%) but allocates a low share of total spending to education (10.9%)."
      }), _jsx(_components.p, {
        children: "But, altogether, about one in three countries with recent data did not meet either benchmark. Play these data over time to see how spending fluctuates over the years."
      })]
    }), "\n", _jsx(Figure, {
      id: "GOVVGDP"
    }), "\n", _jsx(_components.p, {
      children: "The main source of government revenue is taxation, even in low-income countries receiving a high share of income from grants (i.e. aid). Overall, average government revenue as a share of GDP was 14% in low-income, 18% in lower-middle-, 22% in upper-middle- and 33% in high-income countries. Even if all countries assigned the same priority to education in their budget, for instance, countries that raise more taxes and other domestic resources will spend more on education."
    }), "\n", _jsx(_components.p, {
      children: "Having said that, the large variation in the amount spent on education between countries with otherwise similar characteristics is an indication that there is nothing pre-determined in these relationships. Some countries choose to spend more on education to achieve their objectives. And we should highlight those that do not."
    }), "\n", _jsx(_components.h2, {
      id: "household-expenditure",
      children: "Household expenditure"
    }), "\n", _jsx(_components.p, {
      children: "Households spend a significant amount to support their children’s education – and more so in poorer than in richer countries."
    }), "\n", _jsx(_components.p, {
      children: "This spending can be in the form of tuition and other fees, but can also be for education goods and services, of which some are required (e.g. textbooks) and others are not (e.g. private lessons)."
    }), "\n", _jsxs(IndicatorReference, {
      id: "4.5.4",
      children: [_jsx(_components.p, {
        children: "Where such data is standardized across countries, spectacular evidence emerges of the contribution that households are making to total education spending. This can change the way countries’ efforts on education are understood."
      }), _jsx(_components.p, {
        children: "In low-income countries, households spend less than 1% of their budgets on education, constrained by economic hardship. In contrast, households in lower-middle-income countries allocate a median of 2.9% and an unweighted mean of 3.6% of their spending to education, with their spending distribution being the widest among all income groups. The high level of spending in lower-middle-income countries suggests that households face pressures to supplement inadequate public funding. In upper-middle-income countries, the median share of household budgets spent on education drops to 1.5%, with an unweighted mean of 2.3%. Finally, households in high-income countries dedicate a median of 1.3% or an unweighted mean of 1.7% to education."
      }), _jsxs(ScrollyArea, {
        children: [_jsx(ScrollySticky, {
          children: _jsx(Figure, {
            id: "GOVHH_ABS_REL"
          })
        }), _jsx(ScrollySection, {
          id: "absolute,BGD,FRA",
          children: _jsxs(_components.p, {
            children: ["For example, in 2019, ", _jsx(_components.strong, {
              children: "Bangladesh"
            }), " had one of the lowest levels of ", _jsx(HighlightByIndex, {
              index: "0",
              children: "government expenditure"
            }), " as a percentage of GDP. Yet, once ", _jsx(HighlightByIndex, {
              index: "1",
              children: "household contributions"
            }), " are taken into account, Bangladesh’s total national expenditure on education as a percentage of GDP was similar to that of ", _jsx(_components.strong, {
              children: "France"
            }), "."]
          })
        }), _jsx(ScrollySection, {
          id: "absolute,IND,BEL",
          children: _jsxs(_components.p, {
            children: ["The government of ", _jsx(_components.strong, {
              children: "India"
            }), " spent about 2.4 ", _jsx(HighlightByIndex, {
              index: "0",
              children: "percentage points of GDP"
            }), " less on education than did ", _jsx(_components.strong, {
              children: "Belgium"
            }), " – the blue bar –, but India spent overall more than Belgium because its ", _jsx(HighlightByIndex, {
              index: "1",
              children: "households"
            }), " spent more than 2.6 percentage points of GDP on education – the red bar."]
          })
        }), _jsx(ScrollySection, {
          id: "relative,NGA,LBN,HTI",
          children: _jsxs(_components.p, {
            children: ["The ", _jsx(HighlightByIndex, {
              index: "1",
              children: "share of households"
            }), " in total education expenditure rises to over 70% in some countries, including ", _jsx(_components.strong, {
              children: "Nigeria"
            }), ", ", _jsx(_components.strong, {
              children: "Haiti"
            }), " and ", _jsx(_components.strong, {
              children: "Lebanon"
            }), "."]
          })
        })]
      })]
    }), "\n", _jsx(_components.h2, {
      id: "conclusion",
      children: "Conclusion"
    }), "\n", _jsx(_components.p, {
      children: "The Global Education Monitoring Report team has long argued that understanding global education spending requires a joint examination of the three main sources of education funding: domestic public funding, external public funding and private funding. There is therefore a need for timely and consistent data from low- and middle-income countries on how much is being spent on education and from what sources."
    }), "\n", _jsx(_components.p, {
      children: "In addition to increasing the funds available for education, we also need to strengthen equitable distribution. In order to achieve the global education goal, SDG 4, it is important that countries and donors take responsibility and fulfil their commitments."
    }), "\n", _jsx(RelatedMaterial, {
      children: _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://www.unesco.org/gem-report/en/2024efw",
            children: "Education Finance Watch 2024"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://en.unesco.org/gem-report/financing-for-equity",
            children: "Unlocking Financing for Equity in Education"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://education-profiles.org/",
            children: "Profiles Enhancing Education Reviews (PEER)"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://en.unesco.org/gem-report/node/3141",
            children: "COVID-19 is a Serious Threat to Aid to Education Recovery"
          })
        }), "\n"]
      })
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
